import { supabase } from "../../lib/supabase";
import { getSupportedColumns } from "../../lib/schema";
import type { MediaItem } from "@vimos/api-client";

export const mediaBucketName = "media";
export const maxMediaFileSize = 5 * 1024 * 1024;
export const allowedMediaTypes = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/svg+xml",
]);

export function safeMediaFileName(name: string): string {
  const extension = name.includes(".") ? name.split(".").pop() : "img";
  const base = name
    .replace(/\.[^.]+$/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 70);
  return `${base || "imagen"}-${crypto.randomUUID()}.${extension}`;
}

export function mediaPublicUrl(item: MediaItem): string {
  return (
    item.public_url ?? supabase.storage.from(mediaBucketName).getPublicUrl(item.storage_path).data.publicUrl
  );
}

export async function uploadMediaFile(
  file: File,
  altText: string,
  uploadedBy?: string | null,
): Promise<MediaItem> {
  if (!allowedMediaTypes.has(file.type))
    throw new Error("Solo se permiten imagenes JPG, PNG, WebP, GIF o SVG.");
  if (file.size > maxMediaFileSize) throw new Error("La imagen no debe superar 5 MB.");

  const storagePath = `uploads/${new Date().getFullYear()}/${safeMediaFileName(file.name)}`;
  const { error: uploadStorageError } = await supabase.storage
    .from(mediaBucketName)
    .upload(storagePath, file, { cacheControl: "31536000", upsert: false });
  if (uploadStorageError) throw uploadStorageError;

  const { data: publicUrlData } = supabase.storage.from(mediaBucketName).getPublicUrl(storagePath);
  const supportedColumns = await getSupportedColumns("media");
  const payload: Record<string, unknown> = {
    storage_path: storagePath,
    alt_text: altText || null,
    uploaded_by: uploadedBy ?? null,
  };

  const enhancedPayload: Record<string, unknown> = {
    file_name: file.name,
    mime_type: file.type,
    size_bytes: file.size,
    public_url: publicUrlData.publicUrl,
  };

  for (const [key, value] of Object.entries(enhancedPayload)) {
    if (supportedColumns.has(key)) payload[key] = value;
  }

  const { data, error } = await supabase
    .from("media")
    .insert(payload as never)
    .select("*")
    .single();
  if (error) throw error;
  return data as MediaItem;
}

export async function fetchMediaItems(): Promise<MediaItem[]> {
  const { data, error } = await supabase.from("media").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}
